import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {LegibilityStatus} from '@Panels/styles-input-panel/styles-input-panel.types';
import {noop} from '@Utils/general.util';
import type {StylesPanelLegibilityBannerProps} from './styles-panel-legibility-banner.types';

/**
 * Feedback shown in the color sections when a custom background is active:
 * a green "we adjusted the content colors" notice (with dismiss + undo), or a
 * red "these colors are hard to read" warning (with a fix action).
 */
export function StylesPanelLegibilityBanner({legibility, className = ''}: StylesPanelLegibilityBannerProps): ReactElement | null {
  const {status, onUndo = noop, onFix = noop, onDismiss = noop} = legibility;

  const getBannerClasses = (backgroundClass: string): string => {
    return `${backgroundClass} radius-8 spacing-p-3 flex-v-row gap-3 ${className}`;
  };

  /**
   * The icon is sized to the 12px label beside it — left unsized it renders at 24px and dwarfs it.
   *
   * The label wraps to a second line at panel width and takes the row's spare space (`flex-1`), so the
   * icons are the only fixed-width things in it. Both carry `flex-shrink-0` or the wrapped label pushes
   * them out of the banner, and both rows align to the start so an icon sits on the label's first line
   * rather than floating into the gap between the two.
   */
  const renderMessage = (icon: string, toneClass: string, label: ReactElement): ReactElement => {
    return (
      <div className="flexbox flex-items-start gap-2 flex-1">
        <Icon icon={icon} size={IconSize.SIZE_ICON_16} className={`${toneClass} flex-shrink-0`} />
        {label}
      </div>
    );
  };

  const renderMessageText = (messageKey: string, toneClass: string): ReactElement => {
    return <Text val={window.i18next.t(messageKey)} size={TextSize.SM} bold className={`${toneClass} flex-1`} />;
  };

  const renderAdjustedMessageWithUndoLink = (): ReactElement => {
    return (
      <div className="flex-1 body-sm-bold">
        <Text val={window.i18next.t('pmwjs_content_colors_adjusted_for_legibility')} size={TextSize.SM} bold className="ct-success-primary" />{' '}
        <ClickableDiv className="_d-inline _cursor-pointer" onClick={onUndo}>
          <Text val={window.i18next.t('pmwjs_undo_changes')} size={TextSize.SM} bold underline className="ct-success-primary" />
        </ClickableDiv>
      </div>
    );
  };

  if (status === LegibilityStatus.ADJUSTED) {
    return (
      <div className={getBannerClasses('bg-success-secondary-default')}>
        <div className="flexbox flex-items-start flex-justify-between gap-3">
          {renderMessage('icon-info', 'ct-success-primary', renderAdjustedMessageWithUndoLink())}
          <Icon
            icon="icon-close"
            size={IconSize.SIZE_ICON_16}
            className="ct-success-primary flex-shrink-0"
            isUsedAsButton
            onClick={onDismiss}
            ariaLabel={window.i18next.t('pmwjs_dismiss')}
          />
        </div>
      </div>
    );
  }

  if (status === LegibilityStatus.POOR) {
    return (
      <div className={getBannerClasses('bg-error-secondary-default')}>
        {renderMessage('icon-exclamation-triangle', 'ct-error-primary', renderMessageText('pmwjs_content_colors_reducing_legibility', 'ct-error-primary'))}
        <Button type={Type.SECONDARY} size={Size.SMALL} text={window.i18next.t('pmwjs_fix_legibility')} customClasses="_full-width" onClick={onFix} />
      </div>
    );
  }

  /**
   * Message only, and in the warning tone rather than the error one: unlike `POOR` this blocks
   * nothing, and there is no derived alternative to offer — a Mode A page has no background to
   * re-derive a fill from, and D20 settled that a pale fill keeps its own colour.
   */
  if (status === LegibilityStatus.WARNING) {
    return (
      <div className={getBannerClasses('bg-warning-secondary-default')}>
        {renderMessage('icon-exclamation-circle', 'ct-warning-primary', renderMessageText('pmwjs_content_color_hard_to_see_on_page', 'ct-warning-primary'))}
      </div>
    );
  }

  return null;
}
